import React, { useState } from "react";
import { formatDate } from "../../utils/dateUtils.js";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../modals/ConfirmModal.jsx";
import { deleteAlert } from "../../services/alertsService.js";
import { showPromiseToast } from "../../services/toastService.js";
import { LuMailCheck, LuMailX } from "react-icons/lu";
import { getLevelIcon, getPriorityText } from "../../utils/priorityUtils.js";

const AlertView = ({ state, setState, editable }) => {
	const navigate = useNavigate();
	const [showModal, setShowModal] = useState(false);
	const [alertToDelete, setAlertToDelete] = useState(null);
	const now = new Date();
	const activeAlerts = state.filter((alert) => new Date(alert.expire_date) > now).reverse();
	const expiredAlerts = state.filter((alert) => new Date(alert.expire_date) <= now).reverse();

	const handleDeleteClick = (id) => {
		setAlertToDelete(id);
		setShowModal(true);
	};

	const confirmDelete = () => {
		showPromiseToast(deleteAlert(alertToDelete), {
			pending: "Eliminazione allerta in corso...",
			success: "Allerta eliminata con successo",
		})
			.then(() => setState(state.filter((alert) => alert.id !== alertToDelete)))
			.finally(() => {
				setShowModal(false);
				setAlertToDelete(null);
			});
	};

	const handleEdit = (alert) => {
		navigate(`/alerts?tab=new-alert&id=${alert.id}`);
	};

	return (
		<div className="bg-white shadow-md rounded-lg p-6">
			<h2 className="text-2xl font-bold mb-6 text-gray-800">Gestione Allerte</h2>

			{/* Allerte Attive */}
			<h3 className="text-xl font-semibold mb-4 text-green-600">Allerte Attive</h3>
			<ul className="space-y-4">
				{activeAlerts.length > 0 ? (
					activeAlerts.map((alert) => (
						<li
							key={alert.id}
							className="relative border border-gray-300 rounded-md p-4 xl:grid xl:grid-cols-2 lg:gap-6"
						>
							{/* Pulsanti Azione */}
							{editable && (
								<div className="absolute top-2 right-2 flex space-x-2">
									<button
										id="edit"
										aria-label="Modifica"
										className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 shadow"
										onClick={() => handleEdit(alert)}
										title="Modifica"
									>
										Modifica
									</button>
									<button
										id="delete"
										aria-label="Elimina"
										className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
										onClick={() => handleDeleteClick(alert.id)}
										title="Elimina"
									>
										Elimina
									</button>
								</div>
							)}

							<div>
								<h4 className="text-xl font-semibold text-gray-900 flex items-center">
									{alert.title}
									{alert.statusMail === 1 && (
										<span className="text-xs bg-green-500 text-white rounded-full p-1 ml-2 flex items-center justify-center">
											<LuMailCheck className="w-4 h-4" />{" "}
										</span>
									)}
									{alert.statusMail === -1 && (
										<span className="text-xs bg-red-500 text-white rounded-full p-1 ml-2 flex items-center justify-center">
											<LuMailX className="w-4 h-4" />{" "}
										</span>
									)}
								</h4>

								<p className=" text-gray-700 mt-2">
									<strong>Categoria:</strong> {alert.category_name || "N/A"}
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Area:</strong> {alert.area_name || "N/A"}
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Valida dal:</strong> {formatDate(alert.valid_date)}
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Valida fino a:</strong> {formatDate(alert.expire_date)}
								</p>
							</div>
							<div>
								<p className=" text-gray-700 mt-2 flex items-center">
									<strong>Priorità:</strong>
									<span className="mx-2">{getLevelIcon(alert.priority)} </span>
									<span>{getPriorityText(alert.priority)}</span>
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Descrizione:</strong> {alert.description || "N/A"}
								</p>
							</div>
						</li>
					))
				) : (
					<p className="text-gray-500">Nessuna allerta attiva disponibile.</p>
				)}
			</ul>

			{/* Allerte Scadute */}
			<h3 className="text-xl font-semibold mt-6 mb-4 text-red-600">Allerte Scadute</h3>
			<ul className="space-y-4">
				{expiredAlerts.length > 0 ? (
					expiredAlerts.map((alert) => (
						<li
							key={alert.id}
							className="relative border border-gray-300 rounded-md p-4 xl:grid xl:grid-cols-2 lg:gap-6"
						>
							<div>
								<h4 className="text-xl font-semibold text-gray-900 flex items-center">
									{alert.title}
									{alert.statusMail === -1 && (
										<span className="text-xs bg-red-500 text-white rounded-full p-1 ml-2 flex items-center justify-center">
											<LuMailX className="w-4 h-4" />{" "}
										</span>
									)}
									{alert.statusMail === 1 && (
										<span className="text-xs bg-green-500 text-white rounded-full p-1 ml-2 flex items-center justify-center">
											<LuMailCheck className="w-4 h-4" />{" "}
										</span>
									)}
								</h4>
								<p className=" text-gray-700 mt-2">
									<strong>Categoria:</strong> {alert.category_name || "N/A"}
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Area:</strong> {alert.area_name || "N/A"}
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Scaduta il:</strong> {formatDate(alert.expire_date)}
								</p>
							</div>
							<div>
								<p className=" text-gray-700 mt-2 flex items-center">
									<strong>Priorità:</strong>
									<span className="mx-2">{getLevelIcon(alert.priority)} </span>
									<span>{getPriorityText(alert.priority)}</span>
								</p>
								<p className=" text-gray-700 mt-2">
									<strong>Descrizione:</strong> {alert.description || "N/A"}
								</p>
							</div>
						</li>
					))
				) : (
					<p className="text-gray-500">Nessuna allerta scaduta disponibile.</p>
				)}
			</ul>
			{/* Modal di Conferma */}
			{showModal && (
				<ConfirmModal
					message="Sei sicuro di voler eliminare questa allerta?"
					onConfirm={confirmDelete}
					onCancel={() => setShowModal(false)}
				/>
			)}
		</div>
	);
};

export default AlertView;
